import { useEffect, useState } from "react";
import Head from "next/head";
import { catchyViewportContent } from "@catchy/app-core/src/viewport";
import { useRouter } from "next/router";
import useCatchyServiceWorker from "@catchy/app-core/src/useCatchyServiceWorker";
import useLocalStorage from "@catchy/app-core/src/useLocalStorage";
import { initNativeStorageBridge } from "@catchy/app-core/src/nativeStorageBridge";
import { getStorageKey, migrateLegacyLocalStorageKeys } from "@catchy/region-config";
import { isMaplibreSupported, useSafeAreaInsetsReady } from "@catchy/app-core";
if (typeof window !== "undefined") {
  initNativeStorageBridge();
}
function CatchyApp({
  Component,
  pageProps
}) {
  migrateLegacyLocalStorageKeys();
  const [isDark, setIsDark] = useState(false);
  const [mapsSupported, setMapsSupported] = useState(false);
  useEffect(() => {
    const appearance = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = event => setIsDark(event.matches);
    setIsDark(appearance.matches);
    // Keep the legacy listener API for older Safari/native webviews.
    appearance.addListener(onChange);
    return () => appearance.removeListener(onChange);
  }, []);
  useEffect(() => {
    setMapsSupported(isMaplibreSupported());
  }, []);
  const [preferredServices, setPreferredServices] = useLocalStorage(getStorageKey("preferred_services"), []);
  const [preferredStopNames, setPreferredStopNames] = useLocalStorage(getStorageKey("preferred_stop_names"), []);
  const [preferSatellite, setPreferSatellite] = useLocalStorage(getStorageKey("prefer_satellite"), false);
  const [dismissedAlerts, setDismissedAlerts] = useLocalStorage(getStorageKey("dismissed_alerts"), []);
  const [dismissedAlertsLastCleaned, setDismissedAlertsLastCleaned] = useLocalStorage(getStorageKey("dismissed_alerts_last_cleaned"), null);
  useEffect(() => {
    const now = Date.now();
    dismissedAlerts?.length > 5 && now > dismissedAlertsLastCleaned + 604800000 && fetch(`/api/servicealerts/clean-dismissed-alerts?dismissedAlerts=${dismissedAlerts.join()}`).then(response => response.json()).then(result => {
      setDismissedAlerts(result);
    });
    !dismissedAlertsLastCleaned && setDismissedAlertsLastCleaned(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isApple, setIsApple] = useState(false);
  useEffect(() => {
    // Check if the device is an Apple device
    setIsApple(["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod", "MacIntel"].includes(navigator.platform) || navigator.userAgent.includes("Mac") && "ontouchend" in document);
  }, []);
  const router = useRouter();
  const isStartRoute = router.pathname === "/start";
  useEffect(() => {
    if (typeof window !== "undefined" && !sessionStorage.getItem("isInitialLoad")) {
      sessionStorage.setItem("isInitialLoad", "true");
    }
    const handleBeforeUnload = e => {
      const performanceEntries = performance.getEntriesByType("navigation");
      if (performanceEntries.length > 0 && performanceEntries[0].type === "reload") {
        return;
      }
      sessionStorage.setItem("isInitialLoad", "false");
    };
    const handleRouteChange = () => {
      sessionStorage.setItem("isInitialLoad", "false");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);
  const isSafeAreaInsetsSet = useSafeAreaInsetsReady(isStartRoute);
  useEffect(() => {
    const handleResize = () => {
      const vvh = window.visualViewport.height;
      document.documentElement.style.setProperty("--vvh", `${vvh}px`);
    };
    window.visualViewport.addEventListener("resize", handleResize);
    handleResize();
    return () => window.visualViewport.removeEventListener("resize", handleResize);
  }, []);
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);
  useCatchyServiceWorker(router);
  const appContent = isStartRoute ? isSafeAreaInsetsSet ? <Component {...pageProps} /> : null : <>
      <Head>
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="apple-mobile-web-app-title" content="Catchy" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="theme-color" content="#FFFFFF" media="(prefers-color-scheme: light)" />
        <meta name="theme-color" content="#000000" media="(prefers-color-scheme: dark)" />
        <meta name="application-name" content="Catchy" />
        <meta name="mobile-web-app-capable" content="yes" />
        <link rel="apple-touch-icon" sizes="180x180" href="/icons/apple-touch-icon.png" />
        <link rel="manifest" href="/manifest.json" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="alternate icon" href="/favicon.ico" />
        <link rel="mask-icon" href="/icons/mask-icon.svg" color="#ffc20e"></link>
      </Head>

      <Component isApple={isApple} dismissedAlerts={dismissedAlerts} isClient={isClient} isDark={isDark} mapsSupported={mapsSupported} preferredServices={preferredServices} preferredStopNames={preferredStopNames} preferSatellite={preferSatellite} setDismissedAlerts={setDismissedAlerts} setPreferredServices={setPreferredServices} setPreferredStopNames={setPreferredStopNames} setPreferSatellite={setPreferSatellite} {...pageProps} />
    </>;
  return <>
      <Head>
        <meta name="viewport" content={catchyViewportContent} key="viewport" />
      </Head>
      {appContent}
    </>;
}
export default CatchyApp;